/**
 * TanStack Query hooks for /api/v1/field-visibility-rules/.
 *
 * Gated by the same `registration_rules` feature flag as ValidationRule.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import api from '@/admin/lib/api'
import type { PredicateNode } from '@/admin/components/condition-builder/types'
import type { Severity } from '@/admin/hooks/use-validation-rules'
import type { PaginatedResponse } from '@/shared/types/api'

export type FieldVisibilityEffect = 'show' | 'hide' | 'require' | 'optional'

export interface FieldVisibilityRule {
  uuid: string
  key: string
  name: string
  description: string | null
  conditions: PredicateNode | null
  severity: Severity
  message: string
  is_active: boolean
  priority: number
  target_field: string
  target_field_display_name: string
  target_field_code_name: string
  effect: FieldVisibilityEffect
  created_at: string
  updated_at: string
}

export interface FieldVisibilityRuleInput {
  name: string
  description?: string | null
  conditions: PredicateNode | null
  severity: Severity
  message: string
  is_active?: boolean
  priority?: number
  target_field: string
  effect: FieldVisibilityEffect
}

// ---------------------------------------------------------------------------
// Scope classification
//
// Field visibility rules are shared across registration (host) and claims
// scopes on one endpoint. Callers use `detectGroup` to bucket a rule and the
// group sets to filter to a scope - the claims Field Visibility settings tab
// only surfaces when the tenant has at least one claim-scoped rule.
// ---------------------------------------------------------------------------

export const REGISTRATION_GROUPS = new Set(['host'])
export const CLAIM_GROUPS = new Set([
  'installed',
  'job_stop',
  'uninstalled',
  'workmanship',
  'other',
])

export function detectGroup(rule: FieldVisibilityRule): string {
  const key = rule.key ?? ''
  if (key.startsWith('job_stop_field_visible_')) return 'job_stop'
  if (key.startsWith('uninstalled_field_visible_')) return 'uninstalled'
  if (key.startsWith('workmanship_field_visible_')) return 'workmanship'
  if (key.startsWith('claim_field_visible_')) return 'installed'

  const name = rule.name.toLowerCase()
  if (name.includes('structure') || name.includes('host')) return 'host'
  if (name.startsWith('job stop')) return 'job_stop'
  if (name.startsWith('uninstalled')) return 'uninstalled'
  if (name.startsWith('workmanship')) return 'workmanship'
  if (name.startsWith('claim')) return 'installed'

  return 'other'
}

/**
 * True when a rule belongs to the claims scope (as opposed to registration/host
 * scope). Used to decide whether the claims Field Visibility tab has anything
 * to show for this tenant - see `ClaimsSettingsTab`.
 */
export function isClaimScopedFieldVisibilityRule(rule: FieldVisibilityRule): boolean {
  return CLAIM_GROUPS.has(detectGroup(rule))
}

const KEY = ['field-visibility-rules'] as const

export function useFieldVisibilityRules(options?: { enabled?: boolean }) {
  return useQuery<FieldVisibilityRule[]>({
    queryKey: KEY,
    enabled: options?.enabled ?? true,
    queryFn: () =>
      api
        .get<PaginatedResponse<FieldVisibilityRule>>(
          '/v1/field-visibility-rules/',
          { params: { page_size: 200 } },
        )
        .then((r) => r.data.results),
    staleTime: 30_000,
  })
}

export function useCreateFieldVisibilityRule() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: FieldVisibilityRuleInput) =>
      api.post('/v1/field-visibility-rules/', data).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  })
}

export function useUpdateFieldVisibilityRule() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ uuid, ...data }: { uuid: string } & Partial<FieldVisibilityRuleInput>) =>
      api.patch(`/v1/field-visibility-rules/${uuid}/`, data).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  })
}

export function useDeleteFieldVisibilityRule() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (uuid: string) =>
      api.delete(`/v1/field-visibility-rules/${uuid}/`).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  })
}
